import gql from 'graphql-tag'
import {useQuery} from 'react-apollo'
import {Client} from '../../graphql/types'
import {PAMM_OFFER_TREE_FIELDS} from '../PammOffersTree/pammOfferFragment'
import {isAuthorizationDenied} from './authorizationDenied'

/**
 * Fetches both perspectives of the PAMM network in one round trip (PT-5717 / PT-5718).
 *
 * `pammOffers` is the Money Manager's whole network; `pammAgentOffers(includeDownline: true)` is
 * the viewer's own offer plus every offer below it. The scoping of the latter is the server's:
 * ancestors and peer branches are not in the response at all, which is what makes the agent view a
 * confidentiality boundary rather than a rendering choice. Both come back in one query so switching
 * perspective is a state change and not a new page load.
 */
const ACCOUNTS_WITH_PAMM_NETWORK_OFFERS = gql`
  query AccountsWithPammNetworkOffers {
    viewer {
      ... on Client {
        id
        accounts(hidden: false, rejected: false) {
          ... on BaseAccount {
            id
            pammOffers {
              ...PammOfferTreeFields
            }
            pammAgentOffers(includeDownline: true) {
              ...PammOfferTreeFields
            }
          }
        }
      }
    }
  }
  ${PAMM_OFFER_TREE_FIELDS}
`

const isNotNil = <T>(value: T): value is NonNullable<T> => value !== null && typeof value !== 'undefined'

/**
 * Loads the offers backing the Master and Agent views of the PAMM network.
 *
 * Which views a viewer gets is decided by what the server returns, not by a client-side role check.
 * `pammOffers` is refused on any account whose Money Manager the viewer does not own (PT-5720), so
 * a non-empty master tree already means "this viewer is that tree's master" — and an agent's browser
 * never receives a master tree it could be talked into rendering.
 *
 * @param managerAccountId - Pins the master tree to one account, for the manager settings page where
 *   the account comes from the route. Omit on pages that have no account in hand: the viewer's own
 *   manager tree is then used, being the only one they can have been given.
 * @returns Offers per view, the shared loading/error state, and whether a pinned master tree was
 *   refused outright — which the manager page shows as a refusal rather than as an empty network.
 */
export const usePammNetworkOffers = (managerAccountId?: number) => {
  // `errorPolicy: 'all'` is load-bearing, not defensive. The query asks for `pammOffers` on EVERY
  // account the viewer holds, and most are not manager accounts: the server refuses the tree on any
  // account whose Money Manager the viewer does not own (PT-5720), and refuses a foreign node's
  // `investors` (PT-5708). Under the default policy ('none') Apollo discards `data` outright as soon
  // as any GraphQL error is present, so a Money Manager who also invests in someone else's PAMM
  // would see a blank page instead of their own network. Keep the data; the refused accounts simply
  // arrive with `pammOffers: null`.
  const {loading, data, error} = useQuery<{viewer: Client}>(ACCOUNTS_WITH_PAMM_NETWORK_OFFERS, {errorPolicy: 'all'})

  const accounts = (data?.viewer?.accounts ?? []).filter(isNotNil)
  const masterAccount =
    typeof managerAccountId === 'number'
      ? accounts.find((account) => account.id === managerAccountId)
      : accounts.find((account) => (account.pammOffers ?? []).length > 0)

  const masterOffers = (masterAccount?.pammOffers ?? []).filter(isNotNil)
  const agentOffers = accounts.flatMap((account) => account.pammAgentOffers ?? []).filter(isNotNil)

  // Refusals on OTHER accounts are routine and already handled by dropping them, so a refusal only
  // concerns the caller when it left a pinned account without its tree — which is what deep-linking
  // an account whose manager the viewer does not own looks like from here.
  const deniedTree =
    typeof managerAccountId === 'number' && masterOffers.length === 0 && isAuthorizationDenied(error)

  // A per-field authorization error on a foreign node is expected. Only surface `error` when it
  // actually cost us both trees, and never as a bare failure when we know it was a refusal.
  return {
    agentOffers,
    deniedTree,
    error: masterOffers.length === 0 && agentOffers.length === 0 && !deniedTree ? error : undefined,
    loading,
    masterOffers,
  }
}
